import type { PokerHomeGames } from '@ps/web-martech-cms-types';
import type {
  GetFilteredClubInfoRequest,
  GetFilteredClubInfoResponse,
  GetPlayersBasedOnStatusRequest,
  GetPlayersBasedOnStatusResponse,
  GetUserClubsResponse,
  HgClubInfo,
  HgGetPlayersBasedOnStatus
} from '@ps/web-shared-api-types/v0/SwHomeGames';
import type { GetClubMembersRequest } from '@ps/web-shared-api-types/v1-preview/SwHomeGames/GetClubMembersRequest';

import type {
  ExtractDispatchFromModel,
  ExtractSelectFromModel,
  WidgetEffectParams
} from '@swf/core/store';
import { login, logout } from '@swf/core/utils/actions';

import { PKR_AVATAR_COUNT, PKR_LOADERS, PLAYERS_STATUS_LIMIT } from '../constants';
import ENDPOINTS from '../constants/endpoints';
import { ClubPlayerStatus, UserClubRole } from '../types/common';

import type { HomeGamesWidgetModel } from '.';
import { clubOptions } from './effects/clubOptions';
import { fetchClubSeasonRanks } from './effects/fetchClubSeasonRanks';
import { fetchClubSeasonStats } from './effects/fetchClubSeasonStats';
import { fetchClubSeasons } from './effects/fetchClubSeasons';
import { fetchLobbyData } from './effects/fetchLobbyData';
import { fetchPlayerTournamentResults } from './effects/fetchPlayerTournamentResults';
import { fetchRecentTournamentSummaries } from './effects/fetchRecentTournamentSummaries';
import { fetchRegisteredTournaments } from './effects/fetchRegisteredTournaments';
import { fetchScheduledRingGames } from './effects/fetchScheduledRingGames';
import { fetchTableStructures } from './effects/fetchTableStructures';
import { fetchTournamentStructures } from './effects/fetchTournamentStructures';

const effects = {
  async preload(_: undefined, params: WidgetEffectParams<PokerHomeGames>): Promise<void> {
    const { actions, select, rootState, dispatch, widget } = params;
    const isClientAuthenticated = select.session.isClientAuthenticated(rootState);
    const { setLoading, reset }: ExtractDispatchFromModel<HomeGamesWidgetModel> =
      dispatch[widget.uid];

    if (isClientAuthenticated) {
      fetchLobbyData({ initial: true }, params);
    }

    widget.unmount.subscribe(() => reset());

    login(actions)?.subscribe(() => {
      setLoading({ isLoading: true, loaderName: PKR_LOADERS.initial });
      fetchLobbyData({ initial: true }, params);
    });
    logout(actions)?.subscribe(() => reset({ keepError: true }));
  },

  async fetchUserClubs(
    payload: undefined,
    { request, dispatch, widget: { uid }, select, rootState }: WidgetEffectParams<PokerHomeGames>
  ) {
    const widgetDispatch: ExtractDispatchFromModel<HomeGamesWidgetModel> = dispatch[uid];
    const loaderName = PKR_LOADERS.userClubs;
    const isClientAuthenticated = select.session.isClientAuthenticated(rootState);

    if (!isClientAuthenticated) return;

    widgetDispatch.setLoading({ isLoading: true, loaderName });

    const { body } = await request<GetUserClubsResponse>({
      service: ENDPOINTS.clubs,
      http: true,
      method: 'GET'
    });

    widgetDispatch.setUserClubs(body);
    widgetDispatch.setLoading({ isLoading: false, loaderName });
  },

  async fetchClubInfo(
    payload: undefined,
    { request, dispatch, widget: { uid }, select, rootState }: WidgetEffectParams<PokerHomeGames>
  ) {
    const widgetDispatch: ExtractDispatchFromModel<HomeGamesWidgetModel> = dispatch[uid];
    const widgetSelect: ExtractSelectFromModel<HomeGamesWidgetModel> = select[uid];
    const loaderName = PKR_LOADERS.clubInfo;
    const isClientAuthenticated = select.session.isClientAuthenticated(rootState);

    if (!isClientAuthenticated) return;

    const { clubs } = widgetSelect.get(rootState);

    widgetDispatch.setLoading({ isLoading: true, loaderName });

    const {
      body: { filteredClubInfoReply }
    } = await request<GetFilteredClubInfoResponse>({
      service: ENDPOINTS.clubsInfoFilter,
      http: true,
      method: 'GET',
      body: {
        gameFilter: {
          gameCategoryMask: 255,
          clubIds: clubs
            .filter(({ roleForUser }) => roleForUser !== UserClubRole.Suspended)
            .map(({ clubId }: HgClubInfo) => clubId)
        } as GetFilteredClubInfoRequest
      }
    });

    widgetDispatch.setUserClubsAndGames({ clubInfoList: clubs, filteredClubInfoReply });
    widgetDispatch.setLoading({ isLoading: false, loaderName });
  },

  async fetchClubAvatars(
    {
      clubId,
      avatarIncluded = true,
      limit = PKR_AVATAR_COUNT,
      ignoreCache = false
    }: Omit<HgGetPlayersBasedOnStatus, 'clubId'> & {
      clubId: number | string;
      ignoreCache?: boolean;
    },
    { request, dispatch, widget: { uid }, select, rootState }: WidgetEffectParams<PokerHomeGames>
  ) {
    const widgetDispatch: ExtractDispatchFromModel<HomeGamesWidgetModel> = dispatch[uid];
    const loaderName = PKR_LOADERS.playersByClubId(clubId);
    const widgetSelect: ExtractSelectFromModel<HomeGamesWidgetModel> = select[uid];
    const { playersByClubId } = widgetSelect.get(rootState);

    if (!ignoreCache && playersByClubId[clubId]) return;

    widgetDispatch.setLoading({ isLoading: true, loaderName });

    const { body } = await request<GetPlayersBasedOnStatusResponse>({
      service: ENDPOINTS.getPlayerBasedOnStatus,
      body: {
        getPlayersBasedOnStatus: {
          avatarIncluded,
          clubId,
          limit,
          statuses: [ClubPlayerStatus.APPROVED]
        }
      } as GetPlayersBasedOnStatusRequest,
      http: true,
      method: 'GET'
    });

    if (body && body.getPlayersBasedOnStatusReply && body.getPlayersBasedOnStatusReply.playerList) {
      widgetDispatch.setPlayersByClubId({
        clubId,
        response: body?.getPlayersBasedOnStatusReply?.playerList
      });
    }

    widgetDispatch.setLoading({ isLoading: false, loaderName });
  },

  async fetchClubMembers(
    {
      clubId,
      statuses,
      memberType,
      namePrefix,
      limit = PLAYERS_STATUS_LIMIT
    }: Omit<HgGetPlayersBasedOnStatus, 'clubId'> & {
      clubId: number | string;
      memberType: 'approved' | 'pending' | 'blocked';
      namePrefix?: string;
    },
    { request, dispatch, widget: { uid } }: WidgetEffectParams<PokerHomeGames>
  ) {
    const widgetDispatch: ExtractDispatchFromModel<HomeGamesWidgetModel> = dispatch[uid];
    const loaderName = PKR_LOADERS.clubMembers;
    widgetDispatch.setLoading({ isLoading: true, loaderName });

    const { body } = await request<GetPlayersBasedOnStatusResponse>({
      service: ENDPOINTS.getPlayerBasedOnStatus,
      body: {
        getPlayersBasedOnStatus: {
          clubId,
          statuses,
          namePrefix,
          limit
        }
      } as GetClubMembersRequest,
      http: true,
      method: 'GET'
    });

    if (body && body.getPlayersBasedOnStatusReply && body.getPlayersBasedOnStatusReply.playerList) {
      widgetDispatch.setClubMembers({
        response: body?.getPlayersBasedOnStatusReply?.playerList,
        memberType
      });
    }

    widgetDispatch.setLoading({ isLoading: false, loaderName });
  },

  fetchRegisteredTournaments,
  fetchScheduledRingGames,
  fetchClubSeasons,
  fetchClubSeasonStats,
  clubOptions,
  fetchLobbyData,
  fetchRecentTournamentSummaries,
  fetchPlayerTournamentResults,
  fetchTableStructures,
  fetchTournamentStructures,
  fetchClubSeasonRanks
};

export default effects;
